import { useState, useEffect } from "react";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
} from "firebase/firestore";
import {
  Plus,
  GripVertical,
  ScrollText,
  Users,
  CircleCheck,
  EllipsisVertical,
  Eye,
  ActivitySquare,
  RefreshCw,
  CircleX,
  User,
  Clock,
  Calendar,
  X,
} from "lucide-react";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  useSensor,
  useSensors,
  PointerSensor,
  useDroppable,
  DragOverEvent,
  defaultDropAnimationSideEffects,
} from "@dnd-kit/core";
import { useDraggable } from "@dnd-kit/core";
import { AppointmentSkeleton } from "./Appointments/AppointmentSkeleton";
import avatar from "../assets/images/default_avatar.jpg";
import No_Content from "../assets/images/No_Content.svg";
import useAvailablePatient, { PatientAvailability } from "./patients/hooks/usePatient";
import { Patient } from "../types";

import {
  DateSchedule,
  UpcomingAppointment,
  AvailablePatient,
  BaseAppointment,
} from "../types/appointment";
import { VideoCallModal } from "./VideoCall/VideoCallModal";
import { CreateNewAppointment } from "./Appointments/CreateNewAppointment";
import { AppointmentService } from "../services/api/appointment.service";
import PatientsDetails from "./patients/PatientsDetails";
import { useLocation } from "@tanstack/react-router";
import { DateSelector } from "./DateSelector";
import { useAuth } from "../context/AuthContext";
import useTreatedPatient from "./patients/hooks/useTreatedPatient";
import { CallPopup } from "./Appointments/CallPopup";
import toast from "react-hot-toast";
import FindDoctorBox from "./Appointments/FindDoctorBox";
import CancelAppointmentModal from "./patients/CancelAppointmentModal";
import { AuthService } from "../services/api";
import { AppointmentSettings, DoctorDetails, Slot } from "./Appointments/types";

const db = getFirestore();

const generateDateRange = () => {
  const dates: DateSchedule[] = [];

  // Get current date at start of day in local timezone
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 0; i < 14; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);

    // Format the full date in YYYY-MM-DD format
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const fullDate = `${year}-${month}-${day}`;

    dates.push({
      fullDate: fullDate,
      date: date.getDate(),
      day: date.toLocaleDateString("en-US", { weekday: "long" }),
      month: date.toLocaleDateString("en-US", { month: "short" }),
      isToday: i === 0,
      isSelected: i === 0,
    });
  }

  return dates;
};

const DATES = generateDateRange();

const deleteOldAppointments = async () => {
  try {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0); // Normalize to midnight
    const currentTimestamp = currentDate.toISOString(); // Convert to Firestore Timestamp

    const q = query(
      collection(db, "patientAvailability"),
      where("appointmentDate", "<", currentTimestamp)
    );
    const querySnapshot = await getDocs(q);

    const deletePromises = querySnapshot.docs.map((docSnap) =>
      deleteDoc(doc(db, "patientAvailability", docSnap.id))
    );
    await Promise.all(deletePromises);
  } catch (error) {
    console.error("Error deleting old appointments:", error);
  }
};
function DraggableAppointment({
  appointment,
  isDragging,
  isDraggable,
  handleShowDetails,
  onClick,
  handleCancleAppointment,
  handleRescheduleAppointment,
  ...props
}: {
  appointment: UpcomingAppointment;
  isDragging?: boolean;
  isDraggable?: boolean;
  onClick: () => void;
  handleShowDetails: () => void;
  handleCancleAppointment: () => void;
  handleRescheduleAppointment: () => void;
}) {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: appointment.id,
    data: appointment,
  });

  return (
    <div
      ref={setNodeRef}
      className={`mx-2 sm:mx-4 mb-3 px-3 py-3 bg-white border border-gray-100 rounded-lg transition-colors duration-200 select-none ${
        !isDraggable
          ? " cursor-not-allowed"
          : isDragging
          ? "shadow-lg ring-2 ring-emerald-500 ring-opacity-50"
          : "hover:shadow-md"
      }`}
      {...attributes}
      {...props}
    >
      <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
        {/* Draggable Handle */}
        <div
          className={`${
            isDraggable ? "cursor-grab" : "cursor-not-allowed"
          } flex items-center justify-center p-1 active:cursor-grabbing hover:bg-gray-50 rounded transition-colors self-stretch ${
            !isDraggable && "hidden"
          }`}
          {...(isDraggable ? listeners : {})}
          onMouseOver={onClick}
        >
          <GripVertical className="w-6 h-6 text-gray-300" />
        </div>

        <div className="flex-1 min-w-0">
          {/* Patient Info Header */}
          <div className="flex items-center mb-3">
            <div className="flex items-center justify-center bg-emerald-100 rounded-lg p-2 mr-3">
              <Calendar className="w-5 h-5 text-emerald-700" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 truncate">
                {appointment?.patientDetails?.name}
              </h3>
              <div className="flex items-center text-gray-500 text-sm">
                <User size={14} className="mr-1" />
                <span>Patient</span>
              </div>
            </div>
          </div>

          {/* Doctor Info */}
          <div className="bg-gray-50 rounded-lg border border-gray-100 py-2 px-2 mb-3">
            <div className="flex items-start sm:items-center">
              <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-full overflow-hidden mr-4  flex-shrink-0">
                <img
                  src={appointment?.doctorDetails?.profileImage ?? avatar}
                  alt={appointment?.doctorDetails?.name}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 mb-1 truncate">
                  Dr. {appointment?.doctorDetails?.name}
                </p>
                <div className="flex items-center text-emerald-600 text-sm font-medium break-words">
                  <Clock size={14} className="mr-2" />
                  <span>
                    {appointment?.appointment_start_time} -{" "}
                    {appointment?.appointment_end_time}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-1 justify-between">
            <button
              className="min-w-[27%]  flex items-center justify-center py-2 text-xs font-medium px-3 text-red-500 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
              onClick={handleCancleAppointment}
            >
              <CircleX className="w-4 h-4 mr-1 text-red-500" />
              Cancel
            </button>
            <button
              className="min-w-[27%]  flex items-center justify-center py-2 text-xs font-medium px-3 text-blue-500 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
              onClick={handleRescheduleAppointment}
            >
              <RefreshCw className="w-4 h-4 mr-1 text-blue-500" />
              Reschedule
            </button>
            <button
              className="min-w-[27%]  flex items-center justify-center py-2 text-xs font-medium px-3 text-emerald-500 bg-emerald-50 hover:bg-emerald-100 rounded-md transition-colors"
              onClick={handleShowDetails}
            >
              <ActivitySquare className="w-4 h-4 mr-1 text-emerald-500" />
              Vitals
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DraggableAvailablePatient({
  patient,
  isDragging,
  isDraggable,
  setCallNotification,
  isCallModalOpen,
  setIsCallModalOpen,
  handleShowDetails,
  handleCancleAppointment,
  handleRescheduleAppointment,
  isAvailable,
}: {
  patient: PatientAvailability;
  isDragging?: boolean;
  isDraggable?: boolean;
  setCallNotification: (isCallModalOpen: {
    isAvailable: boolean;
    status: boolean;
    message: string;
    patientId: string;
    appointmentId: string;
    roomStatus: string;
    doctorImage?: string;
  }) => void;
  isCallModalOpen: boolean;
  setIsCallModalOpen: (isCallModalOpen: boolean) => void;
  handleShowDetails: () => void;
  handleCancleAppointment: () => void;
  handleRescheduleAppointment: () => void;
}) {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: patient.id,
    data: patient,
  });

  useEffect(() => {
    if (patient.callStatus === "Active") {
      setCallNotification({
        isAvailable: true,
        status: true,
        message: `Incoming call from Doctor ${patient.doctorname}`,
        doctorImage: patient.image || "",
        patientId: patient.id,
        appointmentId: patient.id,
        roomStatus: patient.roomStatus,
      });
    }
  }, [patient, setCallNotification]);

  return (
    <>
      <div
        ref={setNodeRef}
        className={`mx-2 sm:mx-4 mb-3 px-3 py-3 bg-white border border-gray-100 rounded-lg transition-colors duration-200 select-none ${
          !isDraggable
            ? "opacity-75 cursor-not-allowed"
            : isDragging
            ? "shadow-lg ring-2 ring-emerald-500 ring-opacity-50"
            : "hover:shadow-md"
        }`}
        {...attributes}
      >
        <div className="flex items-start gap-2 flex-wrap sm:flex-nowrap">
          {/* Draggable Handle */}
          <div
            className={`${
              isDraggable ? "cursor-grab" : "cursor-not-allowed"
            } flex items-center justify-center p-1 active:cursor-grabbing hover:bg-gray-50 rounded transition-colors self-stretch`}
            {...(isDraggable ? listeners : {})}
          >
            <GripVertical className="w-6 h-6 text-gray-300" />
          </div>

          <div className="flex-1 min-w-0">
            {/* Patient Info Header */}
            <div className="flex items-center mb-2">
              <div className="flex items-center justify-center bg-emerald-100 rounded-lg p-2 mr-3">
                <Calendar className="w-5 h-5 text-emerald-700" />
              </div>

              <div className="min-w-0">
                <h3 className="text-base sm:text-lg font-medium text-gray-900 truncate">
                  {patient?.name}
                </h3>
                <div className="flex items-center text-gray-500 text-sm">
                  <User size={14} className="mr-1" />
                  <span>Patient</span>
                </div>
              </div>

              {isAvailable && (
                <button
                  onClick={() => setIsCallModalOpen(true)}
                  disabled={patient?.callStatus !== "Active"}
                  className="disabled:hover:cursor-not-allowed disabled:opacity-50 ml-auto px-3 py-1 text-sm font-medium text-white bg-red-800 rounded-full hover:bg-red-800/80 transition-colors duration-200"
                >
                  Join Call
                </button>
              )}
            </div>

            {/* Doctor Info */}
            <div className="bg-gray-50 rounded-lg border border-gray-100 py-2 px-2 mb-3">
              <div className="flex items-start sm:items-center">
                <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-full overflow-hidden mr-4  flex-shrink-0">
                  <img
                    src={patient?.image || avatar}
                    alt={patient?.doctorname || "Doctor"}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 mb-1 truncate">
                    Dr. {patient?.doctorname}
                  </p>
                  <div className="flex items-center text-emerald-600 text-sm font-medium break-words">
                    <Clock size={14} className="mr-2" />
                    <span>{patient?.appointmentTime}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-1 justify-between">
              <button
                disabled={patient?.callStatus === "Active"}
                onClick={handleCancleAppointment}
                className="flex items-center justify-center py-2 text-xs font-medium px-3 text-red-500 bg-red-50 hover:bg-red-100 rounded-md transition-colors disabled:pointer-events-none"
              >
                <CircleX className="w-4 h-4 mr-1 text-red-500" />
                Cancel
              </button>
              <button
                disabled={patient?.callStatus === "Active"}
                onClick={handleRescheduleAppointment}
                className="flex items-center justify-center py-2 text-xs font-medium px-3 text-blue-500 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors disabled:pointer-events-none"
              >
                <RefreshCw className="w-4 h-4 mr-1 text-blue-500" />
                Reschedule
              </button>
              <button
                className="flex items-center justify-center py-2 text-xs font-medium px-3 text-emerald-500 bg-emerald-50 hover:bg-emerald-100 rounded-md transition-colors"
                onClick={handleShowDetails}
              >
                <ActivitySquare className="w-4 h-4 mr-1 text-emerald-500" />
                Vitals
              </button>
            </div>
          </div>
        </div>
      </div>

      <VideoCallModal
        isOpen={isCallModalOpen}
        onClose={() => setIsCallModalOpen(false)}
        patient={patient?.patientDetails}
        doctorName={patient?.doctorname}
        doctorProfile={patient.image}
        uid={patient?.uid}
        channelName={patient?.channelName}
      />
    </>
  );
}

function TreatedPatientCard({
  patient,
  handleShowDetails,
}: {
  patient: AvailablePatient;
  handleShowDetails: () => void;
}) {
  return (
    <>
      <div
        className={`mx-2 sm:mx-4 mb-3 px-3 py-3 bg-white border border-gray-100 rounded-lg transition-colors duration-200 select-none`}
      >
        <div className="flex items-start gap-2 flex-wrap sm:flex-nowrap">
          <div className="flex-1 min-w-0">
            {/* Patient Info Header */}
            <div className="flex items-center mb-2">
              <div className="flex items-center justify-center bg-emerald-100 rounded-lg p-2 mr-3">
                <Calendar className="w-5 h-5 text-emerald-700" />
              </div>

              <div className="min-w-0">
                <h3 className="text-base sm:text-lg font-medium text-gray-900 truncate">
                  {patient?.name}
                </h3>
                <div className="flex items-center text-gray-500 text-sm">
                  <User size={14} className="mr-1" />
                  <span>Patient</span>
                </div>
              </div>
            </div>

            {/* Doctor Info */}
            <div className="bg-gray-50 rounded-lg border border-gray-100 py-2 px-2 mb-3">
              <div className="flex items-start sm:items-center">
                <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-full overflow-hidden mr-4  flex-shrink-0">
                  <img
                    src={patient?.image || avatar}
                    alt={patient?.doctorname || "Doctor"}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 mb-1 truncate">
                    Dr. {patient?.doctorname}
                  </p>
                  <div className="flex items-center text-emerald-600 text-sm font-medium break-words">
                    <Clock size={14} className="mr-2" />
                    <span>{patient?.appointmentTime}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions button */}
            <div className="flex flex-wrap gap-1 justify-between">
              <button
                className="flex items-center justify-center py-2 text-xs font-medium px-3 text-emerald-500 bg-emerald-50 hover:bg-emerald-100 rounded-md transition-colors"
                onClick={handleShowDetails}
              >
                <ActivitySquare className="w-4 h-4 mr-1 text-emerald-500" />
                Vitals
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function DroppableArea({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id,
  });

  return (
    <div
      ref={setNodeRef}
      className={`h-full transition-all duration-200 ${
        isOver
          ? "scale-[1.02] ring-2 ring-emerald-500 ring-opacity-50 rounded-lg bg-emerald-50/30"
          : ""
      }`}
    >
      {children}
    </div>
  );
}

export function AppointmentsView() {
  const [showPatientDetails, setShowPatientDetails] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [cancellationComment, setcancellationComment] = useState("");

  const handleViewPatientDetails = (patient: Patient) => {
    setShowPatientDetails(true);
    setSelectedPatient(patient);
  };
  const { mobileNumber, isModalOpen } = useLocation().state;

  const [callNotification, setCallNotification] = useState({
    isAvailable: false,
    status: false,
    message: "",
    patientId: "",
    appointmentId: "",
    roomStatus: "",
    doctorImage: "",
  });

  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [isTreatedPatient, setisTreatedPatient] = useState(false);
  const { user } = useAuth();
  const [isMarkasready, setIsmarkasready] = useState(false);
  const [pendingTransfer, setPendingTransfer] = useState<{
    appointment: BaseAppointment;
    destination: string;
    source: string;
  } | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<DateSchedule>(
    DATES.find((date) => date.isToday) || DATES[0]
  );

  const [upcomingAppointments, setUpcomingAppointments] = useState<
    UpcomingAppointment[]
  >([]);

  const { availabilities: availablePatients } = useAvailablePatient({
    clinicId: user?.id || "",
    date: new Date(selectedDate.fullDate).toISOString(),
  });

  const { treatedPatients } = useTreatedPatient({
    clinicId: user?.id || "",
    date: new Date(selectedDate.fullDate).toISOString(),
  });

  const [activeId, setActiveId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isCreateNewAppointmentOpen, setIsCreateNewAppointmentModalOpen] =
    useState(isModalOpen ? true : false);
  const [showDetailsOnDrop, setShowDetailsOnDrop] = useState(false);
  const [selectedAppointment, setSelectedAppointment] =
    useState<UpcomingAppointment | null>(null);
  const isDraggable = selectedDate.isToday;

  const [cancleAppointmentModal, setCancleAppointmentModal] = useState({
    status: false,
    data: null,
  });
  // For Rescheduling appointments
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorDetails | null>(null);
  const [isSearchingDoctors, setIsSearchingDoctors] = useState(false);
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<Slot | null>(null);
  const [isAppointmentBooked, setisAppointmentBooked] = useState(false);
  const [dateFilter, setDateFilter] = useState<string | null>(null);
  const [isRescheduling, setIsrescheduling] = useState(false);
  const [appointmentForRescheduling, setAppointmentForRescheduling] =
    useState<any>(null);
  const [appointmentCountDate, setAppointmentCountDate] = useState([]);
  const [selectedAppointmentSetting, setSelectedAppointmentSetting] = useState<AppointmentSettings|null>(null);


  const getCurrentTime = () => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, "0")}:${String(
      now.getMinutes()
    ).padStart(2, "0")}`;
  };

  const formattedDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Ensure 2 digits
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const loadAppointmentCount = async () => {
    try {
      const response = await AppointmentService.getPerDayAppointmentCount(
        user?.id
      );
      if (response.success) {
        setAppointmentCountDate(response.data);
      } else {
        setAppointmentCountDate(response.data);
      }
    } catch (error) {
      setAppointmentCountDate([]);
      console.log(error);
      toast.error("Something went Wrong");
    }
  };

  const handleFindDoctors = async (selectedDate?: string) => {
    if (typeof selectedDate !== "string") {
      selectedDate = formattedDate(); // Default to today's date
    }

    setIsSearchingDoctors(true);
    try {
      if (
        user?.role.toLocaleLowerCase() === "clinic" ||
        user?.role.toLocaleLowerCase() === "admin"
      ) {
        await findClinicDoctors(selectedDate);
      } else if (user?.role.toLocaleLowerCase() === "medicalcamp") {
        await findCampDoctors(selectedDate);
      }
    } catch (error) {
      console.error("Error finding doctors:", error);
      toast.error("Failed to find doctors");
    } finally {
      setIsSearchingDoctors(false);
    }
  };

  const findClinicDoctors = async (selectedDate: string) => {
    try {
      const response = await AppointmentService.findAvailableDoctorslots(
        selectedDate,
        "Available"
      );

      if (response.success) {
        if (response.data.length === 0) {
          toast.error("Doctors not Available");
          setAvailableDoctors([]);
          return;
        }

        const todayDate = formattedDate();
        const currentTime = getCurrentTime();

        const filteredData = response.data.map((doctor) => {
          let availableSlots = doctor.available_slots;

          if (selectedDate === todayDate) {
            availableSlots = availableSlots.filter(
              (slot) => slot.start_time > currentTime
            );
          }
          return { ...doctor, available_slots: availableSlots };
        });

        setAvailableDoctors(
          filteredData.filter(
            (available: unknown) => !available?.doctorDetails?.isdecommissioned
          )
        );
      }
    } catch (error) {
      console.error("Error finding clinic doctors:", error);
      toast.error("Failed to find clinic doctors");
    }
  };

  const findCampDoctors = async (selectedDate: string) => {
    try {
      const [campResponse, doctorsResponse] = await Promise.all([
        MedicalCampService.getMedicalCamps({ page: 1, pageSize: 200 }),
        AppointmentService.findAvailableDoctorslots(selectedDate, "Available"),
      ]);

      if (!campResponse.success || !doctorsResponse.success) {
        toast.error("Failed to fetch data");
        return;
      }

      const userCamp = campResponse.data?.dataList.find(
        (camp) => camp.username === user?.username
      );

      if (!userCamp) {
        toast.error("No medical camp assigned to this user");
        return;
      }

      const campDoctors = userCamp.campDoctors || [];
      const todayDate = formattedDate();
      const currentTime = getCurrentTime();

      const matchedDoctors = campDoctors
        .map((campDoctor) => {
          const doctorDetails = doctorsResponse.data?.find(
            (doctor) => doctor.doctor_id === campDoctor.doctorId
          );

          if (!doctorDetails) return null;

          let availableSlots = doctorDetails.available_slots || [];

          // If the selected date is today, filter out past slots
          if (selectedDate === todayDate) {
            availableSlots = availableSlots.filter(
              (slot) => slot.start_time > currentTime
            );
          }

          return {
            ...doctorDetails,
            available_slots: availableSlots,
            amount: campDoctor.amount,
          };
        })
        .filter(Boolean);

      setAvailableDoctors(matchedDoctors);
    } catch (error) {
      console.error("Error finding camp doctors:", error);
      toast.error("Failed to find camp doctors");
    }
  };

  const handleDateFilterOnChange = (selectedDate: string) => {
    handleFindDoctors(selectedDate);
    setDateFilter(selectedDate);
  };

  const handleCloseReschedulingPopup = () => {
    setIsrescheduling(false);
    setSelectedDoctor(null);
    setIsSearchingDoctors(false);
    setSelectedTimeSlot(null);
  };
  //For Rescheduling appointments

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: selectedDate.isToday
        ? {
            distance: 3,
            tolerance: 5,
            delay: 0,
          }
        : {
            distance: 9999,
          },
    })
  );

  // Updating the Appointment status back to book when user drag and drop the component from Available to UpCommming

  const handleUpdateAppointmentStatusOnDrop = async (appointmentId: string) => {
    try {
      const response = await AppointmentService.updateAppointmentStatus(
        appointmentId,
        "Booked"
      );
      if (response.success) {
        // Remove the current appointment id from firebase
        await deleteDoc(doc(db, "patientAvailability", appointmentId));
      } else {
        toast.error("Falied to update appointment status");
      }
    } catch (error) {
      console.error(error);
      toast.error("Falied to update appointment status");
    } finally {
      loadAppointmentsAndCountOnSuccess();
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [bookedResponse] = await Promise.all([
        AppointmentService.getAppointmentListing(
          selectedDate.fullDate,
          "Booked",
          user?.id || ""
        ),
      ]);

      if (bookedResponse.success) {
        setUpcomingAppointments(bookedResponse.data || []);
      } else {
        setUpcomingAppointments([]);
      }
    } catch (error) {
      console.error("Error fetching appointment listings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    if (isDragging) {
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
    } else {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    };
  }, [isDragging]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    setIsDragging(true);
    document.body.style.cursor = "grabbing";
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    if (over?.id === "available-patients") {
      document.body.style.cursor = "copy";
    } else {
      document.body.style.cursor = "grabbing";
    }
  };

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    if (over.id === "upcoming-appointments") {
      const draggedPatient = availablePatients.find((p) => p.id === active.id);
      if (!draggedPatient) return;

      // Call API when dragging from Available to Upcoming
      // Assuming you have an API function like transferToUpcoming
      handleUpdateAppointmentStatusOnDrop(draggedPatient.appointmentId);
    } else if (over.id === "available-patients") {
      const draggedAppointment = upcomingAppointments.find(
        (a) => a.id === active.id
      );
      if (!draggedAppointment) return;

      // Keep existing behavior for Upcoming to Available transfer
      setPendingTransfer({
        appointment: draggedAppointment,
        destination: "available-patients",
        source: "upcoming-appointments",
      });
      setSelectedAppointment(draggedAppointment);
      setShowDetailsOnDrop(true);
    }
  }

  useEffect(() => {
    if (isMarkasready && pendingTransfer) {
      const { appointment, destination } = pendingTransfer;

      if (destination === "available-patients") {
        setUpcomingAppointments((prev) =>
          prev.filter((app) => app.id !== appointment.id)
        );
      }
      setPendingTransfer(null);
      setIsmarkasready(false);
    }
  }, [isMarkasready, pendingTransfer]);

  const handleDateChange = (newDate: DateSchedule) => {
    setSelectedDate(newDate);
  };

  const handleCreateNewAppointment = () => {
    setIsCreateNewAppointmentModalOpen(true);
  };

  const handleAppointmentClick = (appointment: UpcomingAppointment) => {
    setSelectedAppointment(appointment);
  };

  const handleShowPatientDetailsOnClicni = (
    status: boolean,
    appointment: any,
    isTreated?: boolean
  ) => {
    setShowDetailsOnDrop(status);
    setSelectedAppointment(appointment);
    setisTreatedPatient(isTreated || false);
  };

  useEffect(() => {
    deleteOldAppointments();
    if (treatedPatients.some((p) => p.roomStatus === "ENDED")) {
      setCallNotification({
        status: false,
        message: "",
        doctorImage: "",
        patientId: "",
        appointmentId: "",
        roomStatus: "",
        isAvailable: false,
      });
    }
  }, [treatedPatients]);


  // select appointment for rescheduling
  const handleAppointmentSelectionForRescheduling = (appointment: any) => {
    setIsrescheduling(true);
    setAppointmentForRescheduling(appointment);
  };

  // handleRescheduleAppointment
  const handleRescheduleAppointment = async () => {
    // e.prevenetDefault();
    console.log("Selected Appointment Setting:", selectedAppointmentSetting);
    const appointmentData = {
      clinicId: user?.id,
      doctorId: selectedDoctor?.userId  || '',
      patientId:
        appointmentForRescheduling?.patient_id ||
        appointmentForRescheduling?.patientId,
      appointmentDate:
        dateFilter ||
        appointmentForRescheduling?.appointment_date?.split("T")[0] ||
        appointmentForRescheduling?.appointmentDate?.split("T")[0],
      startTime: selectedTimeSlot?.start_time,
      endTime: selectedTimeSlot?.end_time,
      appointmentId: appointmentForRescheduling?.id,
      consultationType: selectedAppointmentSetting?.isOnline ? "Online" : "Offline",
      appointmentSettingClinicId: selectedAppointmentSetting?.clinicId,
    };

    console.log("Appointment Data:", appointmentData);

    // try {
    //   const response = await AppointmentService.rescheduleAppointment(
    //     appointmentData
    //   );
    //   if (response.success) {
    //     toast.success("Appointment Rescheduled Successfully");
    //     // Remove the patient availability from the firebase
    //     await deleteDoc(
    //       doc(db, "patientAvailability", appointmentData?.appointmentId)
    //     );
    //     setisAppointmentBooked(true);
    //   } else {
    //     toast.error("Failed to Rescheduled Appointment");
    //   }
    //   console.log("Selected Doctor:", selectedDoctor);
    //   if (selectedDoctor && response.success) {
    //   const tokens = await AuthService.fetchNotificationTokens(selectedDoctor);
    //   const title = "Appointment rescheduled";
    //   const message = selectedTimeSlot 
    //     ? `Appointment rescheduled on ${dateFilter ?? formattedDate()} ${selectedTimeSlot.slot.start_time}-${selectedTimeSlot.slot.end_time} for ${appointmentForRescheduling?.patientDetails?.name}` 
    //     : "Appointment rescheduled";

    //   if (typeof tokens === "string") {
    //     await AuthService.sendNotification(title, message, tokens);
    //   }
    //   try {
    //     await AuthService.createNotification(title, message, selectedDoctor, "Doctor");
    //   } catch (dbNotifyErr) {
    //     console.error("Error creating notification record:", dbNotifyErr);
    //   }
    // } 
    // } catch (error) {
    //   toast.error(error.message || "Failed to Rescheduled appointment");
    // } finally {
    //   setIsrescheduling(false);
    //   loadAppointmentsAndCountOnSuccess();
    // }
  };

  // handleCancleAppointment
  const handleCancleAppointment = async (appointmentId: string) => {
    try {
      const response = await AppointmentService.cancleAppointment(
        appointmentId
      );

      if (response.success) {
        toast.success(response.message);
        // Remove the patient availability from the firebase
        await deleteDoc(doc(db, "patientAvailability", appointmentId));
        await AppointmentService.updateAppointmentData(appointmentId, {
          doctorsComment: cancellationComment,
          specialInstructions: null,
          chiefComplaints: null,
          diagnosedDisease: null,
          followUpDate: null,
        });
      }
    } catch (error) {
      toast.error(error?.message || "Failed to cancle appointment");
    } finally {
      loadAppointmentsAndCountOnSuccess();
    }
  };

  useEffect(() => {
    loadAppointmentCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAppointmentsAndCountOnSuccess = () => {
    loadAppointmentCount();
    loadData();
  };

  return (
    <div className="h-full flex flex-col  relative">
      {!isCreateNewAppointmentOpen &&
        !showDetailsOnDrop &&
        !showPatientDetails && (
          <>
            <div className="flex items-center justify-between px-4 py-2 sticky top-0 z-10 bg-[#f8f8f8]">
              <button
                onClick={handleCreateNewAppointment}
                className="inline-flex items-center px-10 py-2 bg-[#199C4D]  font-bold text-white rounded-xl hover:bg-[#15773c] transition-colors"
              >
                <Plus className="w-8 h-8 mr-2" />
                New Appointment
              </button>
              <div className="flex items-center gap-2 ">
                <span className="text-md text-gray-700 font-semibold">
                  {selectedDate.month} {selectedDate.date},
                  {new Date().getFullYear()}
                </span>
                {selectedDate.isToday && (
                  <span className="text-sm font-medium text-gray-400">
                    Today
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between px-4 py-2 bg-[#f8f8f8] ">
              <DateSelector
                dates={DATES}
                selectedDate={selectedDate}
                upcomingAppointments={upcomingAppointments}
                onDateChange={handleDateChange}
                appointmentCountDate={appointmentCountDate}
              />
            </div>
            <DndContext
              sensors={sensors}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
            >
              <div className="flex-1 grid grid-cols-3 gap-3 px-2 py-2 bg-[#f8f8f8]  z-10">
                <DroppableArea id="upcoming-appointments">
                  <div className="bg-[#EEF2F5] rounded-lg h-full relative overflow-hidden overflow-y-auto">
                    <div className="p-4 border-b sticky top-0 bg-white z-10">
                      <div className="flex items-center justify-between">
                        <h2 className="text-sm font-medium text-gray-900 flex flex-row gap-2">
                          <ScrollText className="h-5 w-5" />
                          Appointments {selectedDate.isToday && "Today"} -{" "}
                          {upcomingAppointments.length}
                        </h2>
                      </div>
                    </div>
                    <div className="divide-y py-4">
                      {isLoading ? (
                        <AppointmentSkeleton />
                      ) : (
                        <>
                          {upcomingAppointments.length === 0 ? (
                            <div className="w-full h-[250px] flex flex-col items-center justify-center text-gray-500 mt-16">
                              <img
                                src={No_Content}
                                alt="No Patients Available"
                                className="w-24 h-24"
                              />
                              <p className="font-medium text-sm mt-2">
                                No Appointments left for today
                              </p>
                            </div>
                          ) : (
                            <>
                              {upcomingAppointments.map((appointment) => (
                                <DraggableAppointment
                                  key={appointment.id}
                                  appointment={appointment}
                                  handleShowDetails={() =>
                                    handleShowPatientDetailsOnClicni(
                                      true,
                                      appointment
                                    )
                                  }
                                  isDragging={activeId === appointment.id}
                                  isDraggable={isDraggable}
                                  onClick={() =>
                                    handleAppointmentClick(appointment)
                                  }
                                  handleCancleAppointment={() => {
                                    setCancleAppointmentModal({
                                      status: true,
                                      data: appointment,
                                    });
                                  }}
                                  handleRescheduleAppointment={() =>
                                    handleAppointmentSelectionForRescheduling(
                                      appointment
                                    )
                                  }
                                />
                              ))}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </DroppableArea>

                <DroppableArea id="available-patients">
                  <div className="bg-[#EEF2F5] rounded-lg h-full relative overflow-hidden overflow-y-auto">
                    <div className="p-4 border-b sticky top-0 bg-white z-10">
                      <div className="flex items-center justify-between">
                        <h2 className="text-sm font-medium text-gray-900 flex flex-row gap-2">
                          <Users className="h-5 w-5" />
                          Available Patients in Clinic -
                          {availablePatients.length}
                        </h2>
                      </div>
                    </div>
                    <div className="divide-y py-4">
                      {isLoading ? (
                        <AppointmentSkeleton />
                      ) : (
                        <>
                          {availablePatients.length === 0 ? (
                            <div className="w-full h-[250px] flex flex-col items-center justify-center text-gray-500 mt-16">
                              <img
                                src={No_Content}
                                alt="No Patients Available"
                                className="w-24 h-24"
                              />
                              <p className="font-medium text-sm mt-2">
                                No Patients Available in Clinic
                              </p>
                            </div>
                          ) : (
                            <>
                              {availablePatients.map((patient) => (
                                <DraggableAvailablePatient
                                  key={patient?.id || patient?.appointmentId}
                                  patient={patient}
                                  isDraggable={
                                    isDraggable &&
                                    patient.callStatus !== "Active"
                                  }
                                  isDragging={
                                    activeId === patient?.appointmentId
                                  }
                                  isAvailable={true}
                                  setCallNotification={setCallNotification}
                                  isCallModalOpen={isCallModalOpen}
                                  setIsCallModalOpen={setIsCallModalOpen}
                                  handleShowDetails={() =>
                                    handleShowPatientDetailsOnClicni(
                                      true,
                                      patient
                                    )
                                  }
                                  handleCancleAppointment={() => {
                                    setCancleAppointmentModal({
                                      status: true,
                                      data: patient,
                                    });
                                  }}
                                  handleRescheduleAppointment={() =>
                                    handleAppointmentSelectionForRescheduling(
                                      patient
                                    )
                                  }
                                />
                              ))}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </DroppableArea>

                <DroppableArea id="treated-patients">
                  <div className="bg-[#EEF2F5] rounded-lg h-full relative overflow-auto">
                    <div className="p-4 border-b sticky top-0 bg-white z-10">
                      <div className="flex items-center justify-between">
                        <h2 className="text-sm font-medium text-gray-900 flex flex-row gap-2">
                          <CircleCheck className="w-5 h-5" />
                          Treated Patients - {treatedPatients.length}
                        </h2>
                      </div>
                    </div>
                    <div className="py-4">
                      {isLoading ? (
                        <AppointmentSkeleton />
                      ) : (
                        <>
                          {treatedPatients.length === 0 ? (
                            <div className="w-full h-[250px] flex flex-col items-center justify-center text-gray-500 mt-16">
                              <img
                                src={No_Content}
                                alt="No Patients Available"
                                className="w-24 h-24"
                              />
                              <p className="font-medium text-sm mt-2">
                                No Treated Patients
                              </p>
                            </div>
                          ) : (
                            <>
                              {treatedPatients.map((patient) => (
                                <TreatedPatientCard
                                  key={patient?.id || patient?.appointmentId}
                                  patient={patient}
                                  handleShowDetails={() =>
                                    handleViewPatientDetails(patient)
                                  }
                                />
                              ))}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </DroppableArea>
              </div>

              <DragOverlay
                dropAnimation={{
                  duration: 150,
                  easing: "ease-out",
                  sideEffects: defaultDropAnimationSideEffects({
                    styles: {
                      active: {
                        opacity: "0.4",
                      },
                    },
                  }),
                }}
              >
                {activeId && (
                  <div className="bg-white rounded-lg shadow-xl border p-6 transform scale-105 rotate-1 select-none">
                    {(() => {
                      const item =
                        upcomingAppointments.find(
                          (app) => app.id === activeId
                        ) ||
                        availablePatients.find((pat) => pat.id === activeId);
                      if (!item) return null;

                      return (
                        <div className="flex items-start justify-between gap-6">
                          <div className="flex gap-3">
                            <div className="flex items-center">
                              <GripVertical className="w-5 h-5 text-gray-400" />
                            </div>
                            <div className="space-y-3">
                              <div className="space-y-1">
                                <div className="flex items-center gap-3">
                                  <span className="text-lg font-semibold text-gray-900">
                                    {item?.name || item?.patientDetails?.name}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </DragOverlay>
            </DndContext>
          </>
        )}

      <CreateNewAppointment
        isOpen={isCreateNewAppointmentOpen}
        onClose={() => setIsCreateNewAppointmentModalOpen(false)}
        loadAppointments={loadData}
        loadAppointmentCount={loadAppointmentCount}
        mobileNumberProp={mobileNumber}
      />

      {showDetailsOnDrop && (
        <div className="hiddenScrollbar bg-white rounded-lg  absolute top-0 left-0 w-full h-full z-20 overflow-hidden overflow-y-auto ">
          <PatientsDetails
            showHistory={false}
            goToNewAppointmentModal={() =>
              setIsCreateNewAppointmentModalOpen(true)
            }
            onClose={() => setShowDetailsOnDrop(false)}
            appointment={selectedAppointment}
            loadAppointments={loadAppointmentsAndCountOnSuccess}
            setIsmarkasready={setIsmarkasready}
            destination={pendingTransfer?.destination}
            isTreatedPatient={isTreatedPatient}
          />
        </div>
      )}

      {callNotification.status && (
        <CallPopup
          callNotification={callNotification}
          setCallNotification={setCallNotification}
          setIsCallModalOpen={setIsCallModalOpen}
        />
      )}

      {showPatientDetails && (
        <div className="">
          <PatientsDetails
            showHistory={true}
            selectedPatient={selectedPatient}
            onClose={() => {
              setShowPatientDetails(false);
              setSelectedPatient(null);
            }}
          />
        </div>
      )}

      {isRescheduling && (
        <div className="fixed z-50 w-full h-screen top-0 left-0 flex items-center justify-end p-2 bg-black/35">
          <div className="flex items-center justify-start flex-col hiddenScrollbar overflow-hidden overflow-y-auto  border-2 border-green-700/70 bg-white h-[650px] w-[550px] p-2 rounded-md z-50">
            <div className="w-full p-2 flex items-center justify-end">
              <button
                className="transition-colors text-sm hover:text-red-600"
                onClick={handleCloseReschedulingPopup}
              >
                <X className="w-7 h-7 " />
              </button>
            </div>
            <FindDoctorBox
              isSearchingDoctors={isSearchingDoctors}
              availableDoctors={availableDoctors}
              selectedDoctor={selectedDoctor}
              isAppointmentBooked={isAppointmentBooked}
              selectedTimeSlot={selectedTimeSlot}
              setSelectedDoctor={setSelectedDoctor}
              setSelectedTimeSlot={setSelectedTimeSlot}
              handleBookAppointment={handleRescheduleAppointment}
              handleDateFilterOnChange={handleDateFilterOnChange}
              dateFilter={dateFilter}
              handleFindDoctors={handleFindDoctors}
              isVerified={true}
              selectedAppointmentSetting={selectedAppointmentSetting}
              setSelectedAppointmentSetting={setSelectedAppointmentSetting}
            />
          </div>
        </div>
      )}

      <CancelAppointmentModal
        isOpen={cancleAppointmentModal.status}
        onClose={() => {
          setCancleAppointmentModal({
            status: false,
            data: null,
          });
          setcancellationComment("");
        }}
        onConfirm={() =>
          handleCancleAppointment(
            cancleAppointmentModal?.data?.id ||
              cancleAppointmentModal?.data?.appointmentId
          )
        }
        cancellationComment={cancellationComment}
        setcancellationComment={setcancellationComment}
      />
    </div>
  );
}
